import {Component, inject} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Store} from "@ngrx/store";
import {AppState} from "../../store/state/app.state";
import {FETCH_EXPENSES} from "../../store/action/expense.action";
import {
  Employee,
  Expense,
  ExpensePaymentStatusAPIModel,
  ListExpenseAPIRequest,
  PaginatedAPI
} from "../../models/api.model";
import {Observable, Subscription} from "rxjs";
import {getExpenses} from "../../store/selector/expense.selector";
import {PageEvent} from "@angular/material/paginator";
import {ColumnConfig} from "../common/table/table.component";
import {Status} from "../../constants/global-constants";
import {TableService} from "../../services/table.service";
import {
  CLEAR_EXPENSE_PAYMENTS_HISTORY,
  FETCH_EXPENSE_PAYMENTS_HISTORY,
  UPDATE_EXPENSE_PAYMENTS_STATUS
} from "../../store/action/payment.action";
import {PaymentService} from "../../services/payment/payment.service";
import {MatDialog} from "@angular/material/dialog";
import {PaymentHistoryDialogComponent} from "../common/payment-history-dialog/payment-history-dialog.component";
import {EmployeeDetailsDialogComponent} from "../common/employee-details-dialog/employee-details-dialog.component";
import {ExpenseDetailsDialogComponent} from "../common/expense-details-dialog/expense-details-dialog.component";
import {CommonService} from "../../services/common/common.service";
import {FilterProps, FilterType} from "../common/filter-panel/filter-panel.component";

@Component({
  selector: 'app-expense',
  templateUrl: './expenses.component.html',
  styleUrl: './expenses.component.scss'
})
export class ExpensesComponent {
  constructor(private route: ActivatedRoute,
              private appState: Store<AppState>,
              private paymentService: PaymentService,
              private tableService: TableService,
              private commonService: CommonService) {}

  expenses$: Observable<PaginatedAPI<Expense>>
  expensePaymentHistory$: Subscription
  readonly dialog = inject(MatDialog)

  tableColumns: ColumnConfig<Expense>[] = [
    {
      columnDef: 'batchDate',
      header: 'Batch Date',
      cell: (element: Expense) => this.commonService.getDateFromDateTime(element.batchDate),
      sortable: true,
      filterable: false,
      type: 'NUMBER'
    },
    {
      columnDef: 'runNumber',
      header: 'Run #',
      cell: (element: Expense) => element.runNumber,
      sortable: true,
      filterable: false,
      type: 'STRING'
    },
    {
      columnDef: 'id',
      header: 'ER Number',
      cell: (element: Expense) => `<a class="html-anchor-cell" data-action="showExpenseDetails">${element.id}</a>`,
      sortable: true,
      filterable: false,
      type: 'STRING',
      isHtml: true
    },
    {
      columnDef: 'paymentType',
      header: 'Payment Type',
      cell: (element: Expense) => `${element.paymentType == 'CORPORATE_CARD' ? 'Card' : 'Cash'}`,
      sortable: true,
      filterable: false,
      type: 'LIST',
    },
    {
      columnDef: 'currency',
      header: 'Currency',
      cell: (element: Expense) => `${element.currency}`,
      sortable: true,
      filterable: false,
      type: 'STRING'
    },
    {
      columnDef: 'amount',
      header: 'Amount',
      cell: (element: Expense) => this.tableService.buildAmountColumnHTML(element.currency, element.amount),
      sortable: true,
      filterable: false,
      type: 'NUMBER',
      align: 'right',
      isHtml: true
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: Expense) => this.tableService.buildStatusChip(element.status),
      sortable: true,
      filterable: false,
      isHtml: true,
      type: 'LIST',
    },
    {
      columnDef: 'entityId',
      header: 'Legal Entity',
      cell: (element: Expense) => `${element.entityId}`,
      sortable: true,
      filterable: false,
      type: 'NUMBER'
    },
    {
      columnDef: 'employeeId',
      header: 'Employee',
      cell: (element: Expense) => `<a class="html-anchor-cell" data-action="showEmployeeDetails">${element.employee.id} - ${element.employee.displayName}</a>`,
      sortable: true,
      filterable: false,
      type: 'STRING',
      isHtml: true,
      align: 'left'
    },
    {
      columnDef: 'actions',
      type: 'ACTIONS',
      header: 'Actions',
      cell: () => '',
      sortable: false,
      filterable: false,
      actions: [
        {
          label: 'Mark as Rejected',
          action: 'markStatusAsRejectedAction',
          icon: 'delete',
          condition: element => element.status == Status.PAYMENT_PROCESSED || element.status == Status.PAYMENT_CONFIRMED
        },
        {
          label: 'Mark as Ready for Re-processing',
          action: 'markStatusAsReadyForReprocessingAction',
          icon: 'delete',
          condition: element =>
            element.status == Status.REJECTED ||
            element.status == Status.BANKING_INFO_NOT_AVAILABLE
        },
        {
          label: 'Show Payment History',
          action: 'showPaymentHistoryAction',
          icon: 'delete',
          condition: () => true
        },
        {
          label: 'Mark as Write Off',
          action: 'markStatusAsCancelledAction',
          icon: 'delete',
          condition: element =>
            element.status == Status.REJECTED

        },
      ]
    }
  ]

  expenseRequest: ListExpenseAPIRequest = {
    filters: {},
    sort: {
      sortBy: 'createdDatetime',
      sortOrder: 'ASC'
    },
    pageInfo: { pageNo: 0, limit: 50 }
  }

  filters: { [key: string]: string[] | string } = {};
  filterProps: FilterProps[] = [
    {
      field: 'status',
      label: 'Status',
      type: FilterType.SELECT,
      options: Object.values(Status),
      multiple: true,
    },
    {
      field: 'runNumber',
      label: 'Run Number',
      type: FilterType.TEXT,
      multiple: true,
    },
    {
      field: 'entity',
      label: 'Legal Entity',
      type: FilterType.TEXT,
      multiple: true,
    },
    {
      field: 'currency',
      label: 'Currency',
      type: FilterType.TEXT,
      multiple: true,
    },
    {
      field: 'employeeId',
      label: 'Employee ID',
      type: FilterType.TEXT,
    },
    {
      field: 'employeeName',
      label: 'Employee Name',
      type: FilterType.TEXT,
    },
    {
      field: 'processId',
      label: 'Automation Process ID',
      type: FilterType.TEXT,
    },
    {
      field: 'ids',
      label: 'Expense Report Number',
      type: FilterType.TEXT,
      multiple: true
    },
    {
      field: 'reportKey',
      label: 'Report ID',
      type: FilterType.TEXT,
    },
    {
      field: 'paymentType',
      label: 'Payment Type',
      type: FilterType.SELECT,
      options: ["CASH", "CORPORATE_CARD"],
    },
  ];

  onFilterChange(filters: { [key: string]: string[] | string }) {
    this.filters = filters;
    this.getExpenses()
  }

  ngOnInit (){
    const status = this.route.snapshot.queryParamMap.get('status');
    const runNumber = this.route.snapshot.queryParamMap.get('runNumber');
    const entity = this.route.snapshot.queryParamMap.get('entity');
    const currency = this.route.snapshot.queryParamMap.get('currency');
    const processId = this.route.snapshot.queryParamMap.get('processId');
    const paymentStatus = this.route.snapshot.queryParamMap.get('paymentStatus');
    const ids = this.route.snapshot.queryParamMap.get('ids');

    if (status) {
      this.filters['status'] = status.split(',');
    }
    if (runNumber) {
      this.filters['runNumber'] = runNumber.split(',');
    }
    if (entity) {
      this.filters['entity'] = entity.split(',');
    }
    if (currency) {
      this.filters['currency'] = currency.split(',');
    }
    if (processId) {
      this.filters['processId'] = processId;
    }
    if (paymentStatus) {
      this.filters['paymentStatus'] = paymentStatus;
    }
    if (ids) {
      this.filters['ids'] = ids.split(',');
    }

    this.getExpenses();

    this.expensePaymentHistory$ = this.paymentService
      .getExpensePaymentsHistoryFromStore()
      .subscribe((history: ExpensePaymentStatusAPIModel[]) => {
        if (history) {
          this.openPaymentHistoryDialog(history)
        }
      })
  }

  openPaymentHistoryDialog(history: ExpensePaymentStatusAPIModel[]): void {
    const dialogRef = this.dialog.open(PaymentHistoryDialogComponent, {
      width: '900px',
      data: history
    })
    dialogRef.afterClosed().subscribe(result => {
      this.appState.dispatch(CLEAR_EXPENSE_PAYMENTS_HISTORY())
    })
  }

  openEmployeeDetailsDialog(employee: Employee): void {
    this.dialog.open(EmployeeDetailsDialogComponent, {
      width: '600px',
      data: employee
    })
  }

  openExpenseDetailsDialog(details: Expense): void {
    this.dialog.open(ExpenseDetailsDialogComponent, {
      width: '900px',
      data: details
    })
  }

  getExpenses = () => {
    this.expenseRequest = {
      ...this.expenseRequest,
      filters: this.filters
    }
    this.appState.dispatch(FETCH_EXPENSES({ payload: this.expenseRequest }))
    this.expenses$ = this.appState.select(getExpenses())
  }

  onPageChangeExpense(event: PageEvent) {
    this.expenseRequest = {
      ...this.expenseRequest,
      pageInfo: {
        pageNo: event.pageIndex,
        limit: event.pageSize
      }
    }
    this.getExpenses()
  }

  onSortChangeExpense(event: { active: string; direction: string }) {
    if (!event.active || !event.direction) {
      return
    }
    this.expenseRequest = {
      ...this.expenseRequest,
      sort: {
        sortBy: event.active,
        sortOrder: event.direction
      }
    }
    this.getExpenses()
  }

  handleCellClick(event: { column: string; element: any; action: string }, type: string) {
    const action = event.action

    if (action === 'markStatusAsRejectedAction') {
      this.appState.dispatch(
        UPDATE_EXPENSE_PAYMENTS_STATUS({
          status: Status.REJECTED,
          id: event.element.id,
          paymentType: event.element.paymentType,
          statusUpdateInfo: event.element.statusUpdateInfo,
          actionToCallAfterSuccess: () =>
            FETCH_EXPENSES({
              payload: this.expenseRequest
            })
        })
      )
    }

    if (action === 'markStatusAsReadyForReprocessingAction') {
      this.appState.dispatch(
        UPDATE_EXPENSE_PAYMENTS_STATUS({
          status: Status.READY_FOR_REPROCESSING,
          id: event.element.id,
          paymentType: event.element.paymentType,
          statusUpdateInfo: event.element.statusUpdateInfo,
          actionToCallAfterSuccess: () =>
            FETCH_EXPENSES({
              payload: this.expenseRequest
            })
        })
      )
    }

    if (action === 'showPaymentHistoryAction') {
      this.appState.dispatch(FETCH_EXPENSE_PAYMENTS_HISTORY({ id: event.element.id, paymentType: event.element.paymentType }))
    }

    if (action === 'showEmployeeDetails') {
      this.openEmployeeDetailsDialog(event.element.employee);
    }

    if (action === 'showExpenseDetails') {
      this.openExpenseDetailsDialog(event.element)
    }

    if (action === 'markStatusAsCancelledAction') {
      this.appState.dispatch(
        UPDATE_EXPENSE_PAYMENTS_STATUS({
          status: Status.WRITE_OFF,
          id: event.element.id,
          paymentType: event.element.paymentType,
          statusUpdateInfo: event.element.statusUpdateInfo,
          actionToCallAfterSuccess: () =>
            FETCH_EXPENSES({
              payload: this.expenseRequest
            })
        })
      )
    }
  }

  ngOnDestroy() {
    if(this.expensePaymentHistory$) {
      this.expensePaymentHistory$.unsubscribe();
    }
  }
}
